import { useLanguage } from '../../i18n/LanguageContext';

interface PendingChangesBannerProps {
  pendingRequest: {
    status: string;
    createdAt: string;
    proposedChanges?: Record<string, unknown>;
    rejectionReason?: string;
  } | null;
  lastRejected: {
    rejectionReason: string;
    reviewedAt: string;
  } | null;
  onCancel?: () => void;
  onViewChanges?: () => void;
  /** Spec 081a: current values, so the banner can name a staged description change. */
  currentGarage?: { description?: string | null; descriptionAr?: string | null } | null;
}

export default function PendingChangesBanner({ pendingRequest, lastRejected, onCancel, onViewChanges, currentGarage }: PendingChangesBannerProps) {
  const { t } = useLanguage();
  const tf = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  // Spec 081a: name the description fields when the pending request changes them.
  const info = (pendingRequest?.proposedChanges?.garageInfo ?? {}) as Record<string, unknown>;
  const differs = (key: 'description' | 'descriptionAr') =>
    key in info && ((info[key] as string | null) || '') !== ((currentGarage?.[key] as string | null | undefined) || '');
  const descriptionChanges = [
    differs('description') ? tf('profile.info.description', 'Description (English)') : null,
    differs('descriptionAr') ? tf('profile.info.descriptionAr', 'Description (Arabic)') : null,
  ].filter(Boolean) as string[];

  if (pendingRequest && pendingRequest.status === 'PENDING') {
    return (
      <div className="pending-banner pending">
        <div className="pending-banner-icon">⏳</div>
        <div className="pending-banner-text">
          <strong>{t('profile.pending.title')}</strong>
          <p>{t('profile.pending.description')}</p>
          {descriptionChanges.length > 0 && (
            <p className="pending-banner-fields" data-testid="pending-description-fields">
              {tf('profile.pending.includes', 'Includes')}: {descriptionChanges.join(', ')}
            </p>
          )}
          <span className="pending-banner-date">
            {t('profile.pending.submittedOn')}: {new Date(pendingRequest.createdAt).toLocaleDateString()}
          </span>
          <div className="pending-banner-actions">
            {onViewChanges && (
              <button className="pending-banner-btn view" onClick={onViewChanges}>
                {t('profile.pending.viewChanges')}
              </button>
            )}
            {onCancel && (
              <button className="pending-banner-btn cancel" onClick={onCancel}>
                {t('profile.pending.cancel')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (lastRejected) {
    return (
      <div className="pending-banner rejected">
        <div className="pending-banner-icon">❌</div>
        <div className="pending-banner-text">
          <strong>{t('profile.rejected.title')}</strong>
          <p>{t('profile.rejected.reason')}: {lastRejected.rejectionReason}</p>
          <span className="pending-banner-date">
            {t('profile.rejected.reviewedOn')}: {new Date(lastRejected.reviewedAt).toLocaleDateString()}
          </span>
        </div>
      </div>
    );
  }

  return null;
}
